import { EventType } from "@/analytics/analytics-events";
import { EventBarrier } from "@/components/common/event-barrier";
import {
  useApplyEditWaypointsChanges,
  useCancelEditWaypoints,
} from "@/components/r3f/utils/minimap/use-apply-edit-waypoints-changes";
import { useCurrentArea } from "@/modes/mode-data-context";
import {
  enableLocationMarkerCreation,
  selectIsLocationMarkerCreationEnabled,
  setLocationMarkerArea,
} from "@/store/location-marker-slice";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { selectActiveTool } from "@/store/ui/ui-selectors";
import { deactivateTool, ToolName } from "@/store/ui/ui-slice";
import {
  FaroButton,
  FlagPlusIcon,
  HistoryRedoIcon,
  HistoryUndoIcon,
  Toolbar,
  ToolButton,
  ToolGroup,
} from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { assert } from "@faro-lotv/foundation";
import { useCallback, type JSX } from "react";
import { useEditWaypointsUndoRedo } from "./edit-waypoints-store";
import { useDetectEditWaypointsMount } from "./use-detect-edit-waypoints-mount";

/** @returns Overlay for the edit waypoints mode, that is visible if the tool is active */
export function EditWaypointsOverlay(): JSX.Element | null {
  const dispatch = useAppDispatch();

  const activeTool = useAppSelector(selectActiveTool);

  useDetectEditWaypointsMount(activeTool === ToolName.editWaypoints);

  const isLocationMarkerCreationEnabled = useAppSelector(
    selectIsLocationMarkerCreationEnabled,
  );

  const { undo, redo, pastStates, futureStates } = useEditWaypointsUndoRedo();

  const onStopEditing = useCallback(() => {
    dispatch(deactivateTool());
  }, [dispatch]);

  const onCancel = useCancelEditWaypoints(onStopEditing, "toolbar");

  const onApply = useApplyEditWaypointsChanges(onStopEditing, "toolbar");

  const onUndo = useCallback(() => {
    Analytics.track(EventType.undoEditWaypointsChanges);

    undo(1);
  }, [undo]);

  const onRedo = useCallback(() => {
    Analytics.track(EventType.redoEditWaypointsChanges);

    redo(1);
  }, [redo]);

  const { area } = useCurrentArea();
  assert(area, "Active area does not exist.");
  const onClickLocationMarkerButton = useCallback(() => {
    if (!isLocationMarkerCreationEnabled) {
      dispatch(setLocationMarkerArea(area.id));
    }
    Analytics.track(
      isLocationMarkerCreationEnabled
        ? EventType.deactivateLocationMarkerCreation
        : EventType.activateLocationMarkerCreation,
    );
    dispatch(enableLocationMarkerCreation(!isLocationMarkerCreationEnabled));
  }, [area, dispatch, isLocationMarkerCreationEnabled]);

  if (activeTool !== ToolName.editWaypoints) return null;

  return (
    <EventBarrier>
      <Toolbar direction="row" sx={{ alignItems: "center" }}>
        <ToolGroup sx={{ pl: 1, pr: 1 }}>
          <ToolButton
            aria-label="toggle location marker tool"
            selected={isLocationMarkerCreationEnabled}
            onClick={onClickLocationMarkerButton}
            sx={{ borderRadius: 1.5 }}
          >
            <FlagPlusIcon />
          </ToolButton>
        </ToolGroup>
        <ToolGroup sx={{ px: 1, flexDirection: "row", gap: 1 }}>
          <ToolButton
            aria-label="undo waypoint change"
            disabled={pastStates.length === 0}
            onClick={onUndo}
          >
            <HistoryUndoIcon />
          </ToolButton>
          <ToolButton
            aria-label="redo waypoint change"
            disabled={futureStates.length === 0}
            onClick={onRedo}
          >
            <HistoryRedoIcon />
          </ToolButton>
        </ToolGroup>

        <ToolGroup sx={{ p: 1, flexDirection: "row", gap: 1 }}>
          <FaroButton
            aria-label="cancel waypoint changes"
            variant="ghost"
            onClick={onCancel}
            dark
          >
            Cancel
          </FaroButton>

          <FaroButton
            aria-label="apply waypoint changes"
            onClick={onApply}
            dark
          >
            Apply changes
          </FaroButton>
        </ToolGroup>
      </Toolbar>
    </EventBarrier>
  );
}
